import React, { useEffect, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Alert,
  Box,
  CircularProgress,
  Grid,
  Button,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  styled,
} from "@mui/material";
import dayjs from 'dayjs';
import { IconButton, Menu, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Refresh from "@mui/icons-material/Refresh";
import { getAllEmergencyAdmissions } from "../../services/EmergencyAdmissionService";
import { getPatientById } from "../../services/patientService";
import PatientAssignmentModal from "../inPatientDetail/patientAssignment.jsx";
import EmergencyAdmissionModal from "../inPatientDetail/emergencyAdmission.jsx";
import EditEmergencyAdmissionModal from "../inPatientDetail/EditEmergencyAdmission.jsx";
import LinkPatientModal from "../AddPatientModal/emergencyPatientModal.tsx";
import { getAdmissionByPatientId } from "../../services/PatientAdmissionService";
import { downloadStyledTableExcel } from "../../utils/patientTableExcelExport";
import {
  TABLE_ACTIONS_ROW_SX,
  TABLE_ADD_BUTTON_SX,
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../utils/pageLayoutStyles";

const columns = [
  { id: "id", label: "Admission Id" },
  { id: "emergencyId", label: "Emergency Id" },
  { id: "patientName", label: "Patient Name" },
  { id: "attendantName", label: "Attendant Name" },
  { id: "attendantContact", label: "Attendant Contact" },
  { id: "patientState", label: "Patient State" },
  { id: "", label: "Linking Status" },
  // { id: "createdAt", label: "Admission Date" },
  { id: "", label: "Action" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
}));
function EmergencyAdmissionTable( ) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [admissions, setAdmissions] = useState([]);
  const [patientInfo, setPatientInfo] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [open_emergency_modal, setOpenEmergencyModal] = useState(null);
  const [open_emergency_detail_modal, setOpenEmergencyDetailModal] = useState(null);
  const [open_assignment_modal, setOpenAssignmentModal] = useState(null);
  const [open_patient_modal, setOpenPatientModal] = useState(false);
  const [open_newAdmission_modal, setNewAdmissionModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [admissionPatientName, setAdmissionPatientName] = useState("");
  const [admissionPatientContact, setAdmissionPatientContact] = useState(""); 
  const [searchQuery, setSearchQuery] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterLinkingStatus, setFilterLinkingStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);


  useEffect(() => {
    fetchAllEmergencyAdmission();
  }, []);

  const fetchAllEmergencyAdmission = async () => {
    try {
      setFetching(true);
      const res = await getAllEmergencyAdmissions();
      // Service already validates and filters invalid data
      // Ensure res is an array
      const dataArray = Array.isArray(res) ? res : [];
      const sorted = [...dataArray].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
      setAdmissions(sorted);
    } catch (error) {
      console.error("Failed to fetch emergency admissions", error);
      // Set empty array on error to show "No Admissions Found" message
      setAdmissions([]);
    } finally {
      setFetching(false);
    }
  };

  const capitalizeWords = (str) => {
    if (!str) return "";
    return String(str)
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = async (event, row) => {  
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
    setSelectedId(row.id);
    setAdmissionPatientName(row.patientName);      
    setAdmissionPatientContact(row.attendantContact);
    if (row.patientId && row.patientId !== 0) {
      try {
        const data = await getPatientById(row.patientId); 
        setPatientInfo(data);
      } catch (error) {
        console.error("Failed to fetch patient info:", error);
        setPatientInfo(null);
      }
    } else {
      setPatientInfo(null);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const fixMonth = (arr) => {
    if (!arr || arr.length < 3) return null;
    return [arr[0], arr[1] - 1, ...arr.slice(2)];
  };

  const toDateObj = (row) => {
    if (!row.createdAt) return null;
    if (Array.isArray(row.createdAt)) {
      const [y, m, d] = row.createdAt;
      return new Date(y, (m || 1) - 1, d || 1);
    }
    return new Date(row.createdAt);
  };

  const filteredRows = admissions.filter((row)=>{
    const isLinked = Number(row.patientId) > 0;
    
    // Global search across all fields
    if (searchQuery.trim()){
      const q = searchQuery.toLowerCase();
      const values = [
        row.id,
        row.emergencyId,
        row.patientName,
        row.attendantName,
        row.attendantContact,
        row.patientState,
        isLinked ? "linked" : "unlinked",
        isLinked ? "patient is now linked with ipd" : "unlinked"
      ]
        .filter(Boolean)
        .map(v=>String(v).toLowerCase());
      if (!values.some(v=>v.includes(q))) return false;
    }
    
    // Filter by any field (not just name)
    if (filterName.trim()){
      const n = filterName.toLowerCase();
      const searchableFields = [
        row.id,
        row.emergencyId,
        row.patientName,
        row.attendantName,
        row.attendantContact,
        row.patientState,
        isLinked ? "linked" : "unlinked",
        isLinked ? "patient is now linked with ipd" : "unlinked"
      ]
        .filter(Boolean)
        .map(v=>String(v).toLowerCase());
      if (!searchableFields.some(v=>v.includes(n))) return false;
    }
    
    // Filter by State (dropdown)
    if (filterState){
      const rowState = String(row.patientState||"").toLowerCase();
      const filterStateLower = filterState.toLowerCase();
      // Handle multiple state values that might match
      if (filterStateLower === "critical" && rowState !== "critical") return false;
      if (filterStateLower === "serious" && rowState !== "serious" && rowState !== "serious-stable") return false;
      if (filterStateLower === "stable" && rowState !== "stable" && rowState !== "serious-stable") return false;
    }
    
    // Filter by Linking Status
    if (filterLinkingStatus){
      if (filterLinkingStatus === "linked" && !isLinked) return false;
      if (filterLinkingStatus === "unlinked" && isLinked) return false;
    }
    
    if (fromDate || toDate) {
      const d = toDateObj(row);
      if (!d) return false;
      if (fromDate && d < new Date(fromDate)) return false;
      if (toDate) {
        const toEnd = new Date(toDate);
        toEnd.setHours(23, 59, 59, 999);
        if (d > toEnd) return false;
      }
    }
    return true;
  });

  const clearFilters = ()=>{ setSearchQuery(""); setFilterName(""); setFilterState(""); setFilterLinkingStatus(""); setFromDate(""); setToDate(""); };

  const handleExport = async () => {
    const rows = filteredRows || [];
    const headers = ["Admission Id", "Emergency Id", "Patient Name", "Attendant Name", "Attendant Contact", "Patient State", "Linking Status"];
    const tableRows = rows.map((r) => [
      r.id,
      r.emergencyId,
      r.patientName,
      r.attendantName,
      r.attendantContact,
      r.patientState,
      r.patientId && r.patientId !== 0 ? "Linked" : "Unlinked",
    ]);

    await downloadStyledTableExcel({
      filename: `emergency_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Emergency",
      title: "Emergency Admissions",
      tableHeaders: headers,
      tableRows,
    });
  };

  return (
    <Grid container>
      <Grid item xs={12} sx={TABLE_ACTIONS_ROW_SX}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          sx={TABLE_ADD_BUTTON_SX}
          onClick={setOpenEmergencyModal}
        >
          Emergency Admission
        </Button>
      </Grid>

      <Paper sx={{ width: "100%" }}>
        <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
          <Box sx={TABLE_SEARCH_ROW_SX}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e)=>setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters?"Hide filters":"Show filters"}>
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={()=>setShowFilters(v=>!v)}>
                <FilterAltIcon fontSize="small"/>
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                <DownloadIcon fontSize="small"/>
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={fetchAllEmergencyAdmission}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {showFilters && (
          <Stack spacing={1} direction={{ xs:'column', sm:'row' }} alignItems={{ xs:'stretch', sm:'center' }} sx={{ mt:1 }}>
            <TextField size="small" label="Filter by any field" value={filterName} onChange={(e)=>setFilterName(e.target.value)} />
            <TextField
              size="small"
              label="State"
              value={filterState}
              onChange={(e)=>setFilterState(e.target.value)}
              select
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="serious">Serious</MenuItem>
              <MenuItem value="stable">Stable</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Linking Status"
              value={filterLinkingStatus}
              onChange={(e)=>setFilterLinkingStatus(e.target.value)}
              select
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="linked">Linked</MenuItem>
              <MenuItem value="unlinked">Unlinked</MenuItem>
            </TextField>
            <TextField size="small" label="From admission date" type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} InputLabelProps={{shrink:true}} />
            <TextField size="small" label="To admission date" type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} InputLabelProps={{shrink:true}} />
            <Tooltip title="Clear filters">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                <ClearAllIcon fontSize="small"/>
              </IconButton>
            </Tooltip>
          </Stack>
          )}
        </Box>
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    style={{ top: 57, minWidth: column.minWidth }}
                  >
                    {column.label}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {fetching ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Alert severity="info">
                      {admissions.length === 0 
                        ? "No Emergency Admissions Found" 
                        : "No records match your search/filter criteria"}
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const lower = (row.patientState || "").toLowerCase();

                    let stateColor = "inherit";
                    if (lower === "critical") stateColor = "#d32f2f";
                    else if (
                      lower === "serious-stable" ||
                      lower === "serious" ||
                      lower === "stable"
                    )
                      stateColor = "#f9a825";
                    else if (
                      lower === "non-critical" ||
                      lower === "non critical" ||
                      lower === "non"
                    )
                      stateColor = "#2e7d32";
                    const isLinked = Number(row.patientId) > 0;
                    const isRowMenuOpen = openMenu && selectedRow?.id === row.id;
                    return (
                      <TableRow hover key={row.id}>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.emergencyId}</TableCell>
                        <TableCell>{capitalizeWords(row.patientName)}</TableCell>
                        <TableCell>{capitalizeWords(row.attendantName)}</TableCell>
                        <TableCell>{row.attendantContact}</TableCell>
                        <TableCell sx={{ color: stateColor, fontWeight: 600 }}>
                          {capitalizeWords(row.patientState)}
                        </TableCell>
                        <TableCell>
                          {isLinked ? "Patient is now linked with IPD" : "Unlinked"}
                        </TableCell>
                        {/* <TableCell>
                          {Array.isArray(row.createdAt) && row.createdAt.length > 0
                            ? dayjs(fixMonth(row.createdAt)).format('DD MMM YYYY')
                            : row.createdAt
                              ? dayjs(row.createdAt).format('DD MMM YYYY')
                              : ''
                          }
                        </TableCell> */}
                        <TableCell align="center">
                          <IconButton onClick={(e) => handleMenuClick(e, row)}>
                            <MoreVertIcon />
                          </IconButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={isRowMenuOpen}
                            onClose={handleMenuClose}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                          >
                            <>
                              {(!selectedRow?.patientId || Number(selectedRow?.patientId) === 0) ? (
                                <MenuItem
                                  onClick={() => {
                                    handleMenuClose();
                                    setOpenPatientModal(true);
                                    setSelectedId(selectedRow?.id ?? row.id);
                                  }}
                                >
                                  Link Patient
                                </MenuItem>
                              ) : (
                                <MenuItem
                                  onClick={() => {
                                    handleMenuClose();
                                    setOpenPatientModal(true);
                                    setSelectedId(selectedRow?.id ?? row.id);
                                  }}
                                >
                                  View Patient
                                </MenuItem>
                              )}
                              {/* <MenuItem
                                onClick={() => {
                                  handleMenuClose();
                                  setOpenAssignmentModal(true);
                                }}
                              >
                                Emerygency Assignments
                              </MenuItem> */}
                              <MenuItem
                                onClick={() => {
                                  handleMenuClose();
                                  setOpenEmergencyDetailModal(true);
                                }}
                              >
                                Emergency Admission Details
                              </MenuItem>
                            </>
                          </Menu>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
        />
      </Paper>
      <EmergencyAdmissionModal
        open={open_emergency_modal}
        EmergencyId={selectedId}
        handleClose={() => setOpenEmergencyModal(false)}
        onClose={() => setOpenEmergencyModal(false)}
        refreshList={fetchAllEmergencyAdmission}
      />
      <EditEmergencyAdmissionModal
        open={open_emergency_detail_modal}
        EmergencyId={selectedId}
        handleClose={() => setOpenEmergencyDetailModal(false)}
        onClose={() => setOpenEmergencyDetailModal(false)}
        refreshList={fetchAllEmergencyAdmission}
      />
      <PatientAssignmentModal
        open={open_assignment_modal}
        EmergencyId={selectedId}
        handleClose={() => setOpenAssignmentModal(false)}
        onClose={() => setOpenAssignmentModal(false)}
        refreshList={fetchAllEmergencyAdmission}
      />
      <LinkPatientModal
        open={open_patient_modal}
        EmergencyId={selectedId}
        admissionPatientName={admissionPatientName}
        admissionPatientContact={admissionPatientContact}
        handleClose={() => setOpenPatientModal(false)}
        onClose={() => setOpenPatientModal(false)}
        refreshList={async () => {
          await fetchAllEmergencyAdmission();
          // Optimistically update selected row if present
          if (selectedId != null) {
            setAdmissions((prev) => prev.map((r) => (r.id === selectedId ? { ...r, patientId: r.patientId || -1 } : r)));
          }
        }}
        patient_info={patientInfo}
      />
    </Grid>
  );
}

export default EmergencyAdmissionTable;